import { useState, useEffect, useRef } from 'react'
import {
  Image as ImageIcon,
  Search,
  Upload,
  Check,
  AlertCircle,
  X,
  Info
} from 'lucide-react'
import axios from 'axios'
import { categoryManagerApi, CategoryListItem } from '@/lib/api'

export default function CategoryImageUploadPage() {
  // Search state
  const [searchQuery, setSearchQuery] = useState('')
  const [categories, setCategories] = useState<CategoryListItem[]>([])
  const [filteredCategories, setFilteredCategories] = useState<CategoryListItem[]>([])
  const [selectedCategory, setSelectedCategory] = useState<CategoryListItem | null>(null)
  const [loadingCategories, setLoadingCategories] = useState(true)
  const [showDropdown, setShowDropdown] = useState(false)

  // Image state
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Upload state
  const [isUploading, setIsUploading] = useState(false)
  const [uploadResult, setUploadResult] = useState<{ success: boolean; message: string } | null>(null)

  // Load categories on mount
  useEffect(() => {
    loadCategories()
  }, [])

  // Filter categories based on search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredCategories([])
      return
    }

    const query = searchQuery.toLowerCase()
    const filtered = categories.filter(cat =>
      cat.id.toString().includes(query) ||
      cat.display_name.toLowerCase().includes(query)
    ).slice(0, 10) // Limit to 10 results

    setFilteredCategories(filtered)
  }, [searchQuery, categories])

  const loadCategories = async () => {
    try {
      setLoadingCategories(true)
      const data = await categoryManagerApi.listCategories()
      setCategories(data.categories)
    } catch (error) {
      console.error('Error loading categories:', error)
    } finally {
      setLoadingCategories(false)
    }
  }

  const handleSelectCategory = (category: CategoryListItem) => {
    setSelectedCategory(category)
    setSearchQuery(category.display_name)
    setShowDropdown(false)
    setUploadResult(null)
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadResult({ success: false, message: 'Por favor selecciona un archivo de imagen válido' })
      return
    }

    setSelectedFile(file)
    setUploadResult(null)

    // Create preview and get dimensions
    const reader = new FileReader()
    reader.onload = (e) => {
      const url = e.target?.result as string
      setPreviewUrl(url)

      // Get image dimensions
      const img = new window.Image()
      img.onload = () => {
        setImageDimensions({ width: img.width, height: img.height })
      }
      img.src = url
    }
    reader.readAsDataURL(file)
  }

  const handleUpload = async () => {
    if (!selectedCategory || !selectedFile) return

    setIsUploading(true)
    setUploadResult(null)

    try {
      // Check file size before uploading (10MB limit)
      if (selectedFile.size > 10 * 1024 * 1024) {
        setUploadResult({ success: false, message: 'La imagen es demasiado grande (máximo 10MB)' })
        setIsUploading(false)
        return
      }

      const formData = new FormData()
      formData.append('image', selectedFile)
      formData.append('category_id', selectedCategory.id.toString())

      const response = await axios.post('/api/category-manager/upload-category-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        maxBodyLength: Infinity,
        maxContentLength: Infinity
      })

      if (response.data.success) {
        setUploadResult({ success: true, message: response.data.message || 'Imagen subida correctamente' })
        // Clear form after success
        setSelectedFile(null)
        setPreviewUrl(null)
        setImageDimensions(null)
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      } else {
        setUploadResult({ success: false, message: response.data.detail || response.data.message || 'Error al subir la imagen' })
      }
    } catch (error: any) {
      let errorMessage = 'Error de conexión'

      if (error.response?.status === 413) {
        errorMessage = 'La imagen es demasiado grande. Reduce el tamaño o usa una imagen más pequeña.'
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }

      setUploadResult({ success: false, message: errorMessage })
    } finally {
      setIsUploading(false)
    }
  }

  const clearSelection = () => {
    setSelectedCategory(null)
    setSearchQuery('')
    setSelectedFile(null)
    setPreviewUrl(null)
    setImageDimensions(null)
    setUploadResult(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
          <ImageIcon className="w-8 h-8 text-primary-500" />
          Subir Imagen de Categoría
        </h1>
        <p className="text-slate-600 dark:text-slate-400 mt-1">
          Sube una imagen personalizada para una categoría de la tienda
        </p>
      </div>

      {/* Image Size Info */}
      <div className="bg-primary-50 dark:bg-primary-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-primary-600 dark:text-primary-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-medium text-blue-800 dark:text-blue-200">Tamaños de imagen requeridos</h3>
            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
              PrestaShop redimensionará automáticamente la imagen a los siguientes tamaños:
            </p>
            <ul className="text-sm text-blue-700 dark:text-blue-300 mt-2 space-y-1">
              <li><strong>Principal:</strong> 452 x 452 px (medium_default)</li>
              <li><strong>Subcategorías:</strong> 200 x 200 px (category_default)</li>
              <li><strong>Miniaturas:</strong> 100 x 100 px (thumb_default)</li>
              <li><strong>Pequeña:</strong> 98 x 98 px (small_default)</li>
            </ul>
            <p className="text-sm text-primary-600 dark:text-primary-400 mt-2">
              <strong>Recomendación:</strong> Sube una imagen cuadrada de al menos <strong>500 x 500 px</strong> para mejor calidad.
            </p>
          </div>
        </div>
      </div>

      {/* Main Form */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6">
        <div className="space-y-6">
          {/* Category Search */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Buscar Categoría (por ID o nombre)
            </label>
            <div className="relative">
              <div className="flex items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value)
                      setShowDropdown(true)
                      if (selectedCategory && e.target.value !== selectedCategory.display_name) {
                        setSelectedCategory(null)
                      }
                    }}
                    onFocus={() => setShowDropdown(true)}
                    disabled={loadingCategories}
                    placeholder={loadingCategories ? 'Cargando categorías...' : 'Ej: 4969 o "Discos Duros"'}
                    className="w-full pl-10 pr-10 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-gray-400"
                  />
                  {selectedCategory && (
                    <button
                      onClick={clearSelection}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Dropdown */}
              {showDropdown && filteredCategories.length > 0 && !selectedCategory && (
                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredCategories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => handleSelectCategory(cat)}
                      className="w-full px-4 py-2 text-left hover:bg-slate-100 dark:hover:bg-slate-600 text-slate-900 dark:text-white text-sm"
                    >
                      <span className="font-medium text-primary-600 dark:text-primary-400">#{cat.id}</span>
                      <span className="ml-2">{cat.display_name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Selected Category Info */}
            {selectedCategory && (
              <div className="mt-2 p-3 bg-success-50 dark:bg-success-900/20 border border-green-200 dark:border-green-700 rounded-lg">
                <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                  <Check className="w-4 h-4" />
                  <span className="text-sm">
                    Categoría seleccionada: <strong>#{selectedCategory.id}</strong> - {selectedCategory.display_name}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Image Upload */}
          {selectedCategory && (
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Seleccionar Imagen
              </label>

              {/* Drop Zone */}
              <div
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
                  ${previewUrl
                    ? 'border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-900/20'
                    : 'border-slate-300 dark:border-slate-600 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                  }`}
              >
                {previewUrl ? (
                  <div className="space-y-4">
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="max-w-xs max-h-48 mx-auto rounded-lg shadow"
                    />
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      <p><strong>{selectedFile?.name}</strong></p>
                      {imageDimensions && (
                        <p className={imageDimensions.width >= 500 && imageDimensions.height >= 500
                          ? 'text-success-600 dark:text-success-400'
                          : 'text-yellow-600 dark:text-yellow-400'
                        }>
                          Dimensiones: {imageDimensions.width} x {imageDimensions.height} px
                          {imageDimensions.width < 500 || imageDimensions.height < 500
                            ? ' (recomendado: 500x500 mínimo)'
                            : ' ✓'
                          }
                        </p>
                      )}
                    </div>
                    <p className="text-xs text-slate-500">Haz clic para cambiar la imagen</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="w-12 h-12 mx-auto text-slate-400" />
                    <p className="text-slate-600 dark:text-slate-400">
                      Haz clic para seleccionar una imagen
                    </p>
                    <p className="text-xs text-slate-500">
                      JPG, PNG o WebP. Recomendado: 500x500 px mínimo
                    </p>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          )}

          {/* Upload Button */}
          {selectedCategory && selectedFile && (
            <div className="flex justify-end">
              <button
                onClick={handleUpload}
                disabled={isUploading}
                className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg disabled:opacity-50 flex items-center gap-2 font-medium"
              >
                {isUploading ? (
                  <>
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                    Subiendo...
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5" />
                    Subir Imagen
                  </>
                )}
              </button>
            </div>
          )}

          {/* Result Message */}
          {uploadResult && (
            <div className={`p-4 rounded-lg ${
              uploadResult.success
                ? 'bg-success-50 dark:bg-success-900/20 border border-green-200 dark:border-green-700'
                : 'bg-danger-50 dark:bg-danger-900/20 border border-red-200 dark:border-red-700'
            }`}>
              <div className="flex items-center gap-2">
                {uploadResult.success ? (
                  <Check className="w-5 h-5 text-success-600" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-danger-600" />
                )}
                <span className={uploadResult.success ? 'text-green-700 dark:text-green-300' : 'text-danger-700 dark:text-red-300'}>
                  {uploadResult.message}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
